import React, { useMemo, useRef, useState } from 'react';

import { Autocomplete } from '@flixbus/honeycomb-react';

import { CREATE_BREAKDOWN_FLOW_TEXT } from '../texts';

const COPY = CREATE_BREAKDOWN_FLOW_TEXT.breakdownLocation;

export const GROUP_SEGMENT = COPY.groupSegment;
export const GROUP_APPROXIMATE = COPY.groupApproximate;

/**
 * Builds the HC-grouped `options` shape from a flat `{id, label, station}`
 * list. First two entries land under the "segment" group; the rest under
 * "approximate". Empty groups drop out entirely. Exported for tests.
 */
export const buildGroupedOptions = (stationOptions) => {
	if (stationOptions.length === 0) return [];
	const segmentStations = stationOptions.slice(0, 2);
	const ghDummyStations = stationOptions.slice(2);
	const groups = [];
	if (segmentStations.length) groups.push({ group: GROUP_SEGMENT, options: segmentStations });
	if (ghDummyStations.length) {
		groups.push({ group: GROUP_APPROXIMATE, options: ghDummyStations });
	}
	return groups;
};

/**
 * Case-insensitive substring filter over station code / name / title.
 * Preserves the grouped shape — a group with no surviving options is
 * dropped so the header doesn't dangle over an empty section. Group
 * membership is decided by the option's original position in the flat
 * `stationOptions` list (first two → segment, rest → approximate) rather
 * than by the filtered slice, so filtering only-approximate hits doesn't
 * mislabel them as segment stations. Exported for tests.
 */
export const filterGroupedStationOptions = (stationOptions, inputValue) => {
	const trimmed = (inputValue ?? '').trim().toLowerCase();
	if (!trimmed) return buildGroupedOptions(stationOptions);
	const segmentStationIds = new Set(stationOptions.slice(0, 2).map((o) => o.value));
	const searchMatches = stationOptions.filter((opt) => {
		const station = opt.data?.station;
		const searchableFields = [station?.code, station?.name, opt.title].filter(Boolean);
		return searchableFields.some((field) => field.toLowerCase().includes(trimmed));
	});
	const groups = [];
	const segmentSearchMatches = searchMatches.filter((o) => segmentStationIds.has(o.value));
	const ghDummySearchMatches = searchMatches.filter((o) => !segmentStationIds.has(o.value));
	if (segmentSearchMatches.length) {
		groups.push({ group: GROUP_SEGMENT, options: segmentSearchMatches });
	}
	if (ghDummySearchMatches.length) {
		groups.push({ group: GROUP_APPROXIMATE, options: ghDummySearchMatches });
	}
	return groups;
};

/**
 * Breakdown-location autocomplete over the preloaded `locationOptions`
 * assembled by the parent (`deriveBreakdownLocationOptions(segment,
 * xStations)`). No backend query lives inside — the parent owns
 * `useQuery(FETCH_BREAKDOWN_STATIONS)` and hands the full list down.
 *
 * Rows are rendered inside HC's grouped-listbox layout: the first two
 * entries (segment endpoints) sit under "Incident segment stations", the
 * rest sit under "Approximate location". Group headers are visible but
 * non-selectable; HC handles that natively.
 *
 * Local substring filter on every keystroke matches against the station's
 * `code`, `name`, and the raw option label; groups whose options all fail
 * the filter drop out of the list.
 *
 * `value` (id string) prefills the combobox input with the matching
 * option's label so a re-mount / navigation-back keeps the pick visible.
 * Clearing the input entirely also clears the stored id so the full
 * grouped list becomes available again on the next focus.
 *
 * @param {{
 *   options: Array<{ id: string, label: string, station?: object }>,
 *   value: string,
 *   onChange: (id: string) => void,
 *   disabled?: boolean,
 *   label?: string,
 *   placeholder?: string,
 *   testId?: string,
 * }} props
 */
const BreakdownStationAutocomplete = ({
	options,
	value,
	onChange,
	disabled = false,
	label = COPY.autocompleteLabel,
	placeholder = COPY.autocompletePlaceholder,
	testId,
}) => {
	const stationOptions = useMemo(
		() =>
			options.map((opt) => ({
				title: opt.label,
				value: opt.id,
				data: opt,
			})),
		[options]
	);

	const initialInputValue = useMemo(() => {
		const match = options.find((o) => o.id === value);
		return match?.label ?? '';
	}, [options, value]);

	const [inputValue, setInputValue] = useState(initialInputValue);
	const inputFieldRef = useRef(null);

	const groupedOptions = useMemo(
		() => filterGroupedStationOptions(stationOptions, inputValue),
		[stationOptions, inputValue]
	);

	const selectedValue = useMemo(() => {
		const match = stationOptions.find((o) => o.value === value);
		return match ? [match] : [];
	}, [stationOptions, value]);

	const handleInputChange = (event) => {
		const next = event.target.value;
		setInputValue(next);
		// Clearing the input drops the stored selection so the full grouped
		// list becomes available again on the next focus.
		if (value && next === '') {
			onChange('');
		}
	};

	const handleChange = (selectedOptions) => {
		const next = Array.isArray(selectedOptions) ? selectedOptions : [];
		if (next.length === 0) {
			onChange('');
			setInputValue('');
			return;
		}
		const picked = next[0];
		onChange(picked.value);
		setInputValue(picked.title);
	};

	const handleReset = () => {
		onChange('');
		setInputValue('');
		// HC re-focuses the input after reset but keeps the dropdown closed;
		// re-open it explicitly so the user sees the full grouped list right
		// after clearing. Wrapped in rAF because the click event that fires
		// this handler otherwise races HC's own close-on-outside-click logic.
		requestAnimationFrame(() => inputFieldRef.current?.click());
	};

	return (
		<Autocomplete
			value={selectedValue}
			inputValue={inputValue}
			options={groupedOptions}
			onChange={handleChange}
			comboboxProps={{
				label,
				placeholder,
				disabled,
				onChange: handleInputChange,
				resetProps: { label: COPY.autocompleteResetLabel, onClick: handleReset },
				inputFieldRef,
				'data-testid': testId,
			}}
			listBoxProps={{
				label: COPY.autocompleteListLabel,
				optionsToDisplay: 5,
			}}
		/>
	);
};

export default BreakdownStationAutocomplete;
